import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { getImageUrl } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Briefcase, Building2, Users as UsersIcon, ShieldCheck, GraduationCap, Crown } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  athlete: { label: "Athletes", color: "#074572", bg: "#EFF6FF" },
  coach: { label: "Coaches", color: "#F85037", bg: "#FEF2F2" },
  scout: { label: "Scouts", color: "#10B981", bg: "#ECFDF5" },
  club: { label: "Clubs", color: "#8B5CF6", bg: "#F5F3FF" },
};
const EXCLUDED_ROLES = ["admin", "super_admin", "moderator", "verification_admin", "user", "parent"];

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [people, setPeople] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);

  // Sync input when URL changes (e.g. navbar submit)
  useEffect(() => {
    const q = searchParams.get("q") || "";
    setQuery(q);
    setDebouncedQuery(q);
  }, [searchParams]);

  // Debounce typing in the page input
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(query);
      if (query !== (searchParams.get("q") || "")) {
        setSearchParams(query ? { q: query } : {}, { replace: true });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, searchParams, setSearchParams]);

  const fetchResults = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      params.set("limit", 50);
      const [peopleRes, teamsRes] = await Promise.all([
        api.get(`/scout/discover?${params}`),
        api.get(`/teams${q ? `?search=${encodeURIComponent(q)}` : ""}`),
      ]);
      const filteredPeople = (peopleRes.data.users || []).filter(u => !EXCLUDED_ROLES.includes(u.role));
      setPeople(filteredPeople);
      setTeams(teamsRes.data || []);
    } catch {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchResults(debouncedQuery); }, [debouncedQuery, fetchResults]);

  const grouped = people.reduce((acc, u) => {
    const role = u.role || "athlete";
    if (!acc[role]) acc[role] = [];
    acc[role].push(u);
    return acc;
  }, {});
  const roleOrder = ["athlete", "coach", "scout", "club"];

  const totalPeople = people.length;
  const totalTeams = teams.length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-0 py-6" data-testid="search-results-page">
      <div className="mb-6">
        <h1 className="text-2xl font-medium text-zinc-900 mb-3" style={{ fontFamily: 'Outfit' }}>
          {debouncedQuery ? <>Results for <span className="text-[#074572]">"{debouncedQuery}"</span></> : "Search SportsUnity"}
        </h1>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search athletes, coaches, scouts, clubs, teams..."
            className="pl-10 h-11 border-[#E6E6E6] bg-white"
            data-testid="search-page-input"
            autoFocus
          />
        </div>
      </div>

      <Tabs defaultValue="people" className="w-full">
        <TabsList className="bg-transparent border-b border-[#E6E6E6] rounded-none w-full justify-start gap-6 px-0 h-auto pb-0">
          <TabsTrigger value="people" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5" data-testid="search-tab-people">
            <UsersIcon size={14} /> People <span className="text-zinc-400">({totalPeople})</span>
          </TabsTrigger>
          <TabsTrigger value="teams" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5" data-testid="search-tab-teams">
            <Building2 size={14} /> Teams & Clubs <span className="text-zinc-400">({totalTeams})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="people" className="mt-6">
          {loading ? (
            <p className="text-center py-12 text-zinc-400 text-sm">Searching...</p>
          ) : totalPeople === 0 ? (
            <p className="text-center py-12 text-zinc-400 text-sm" data-testid="search-no-people">No people found {debouncedQuery && `for "${debouncedQuery}"`}.</p>
          ) : (
            <div className="space-y-6">
              {roleOrder.map(role => {
                const items = grouped[role];
                if (!items || items.length === 0) return null;
                const meta = ROLE_LABELS[role] || { label: role, color: "#074572", bg: "#EFF6FF" };
                return (
                  <section key={role} data-testid={`search-section-${role}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: meta.bg, color: meta.color }}>
                        {meta.label}
                      </span>
                      <span className="text-xs text-zinc-400">{items.length}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {items.map(u => <PersonCard key={u._id} user={u} />)}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="teams" className="mt-6">
          {loading ? (
            <p className="text-center py-12 text-zinc-400 text-sm">Searching...</p>
          ) : totalTeams === 0 ? (
            <p className="text-center py-12 text-zinc-400 text-sm" data-testid="search-no-teams">No teams found {debouncedQuery && `for "${debouncedQuery}"`}.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {teams.map(t => <TeamCard key={t.id} team={t} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PersonCard({ user }) {
  const initials = user.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  const avatarUrl = getImageUrl(user.profile_image);
  return (
    <Link to={`/profile/${user._id}`} data-testid={`search-person-${user._id}`}>
      <div className="bg-white border border-[#E6E6E6] rounded-lg p-3 flex items-center gap-3 hover:border-[#074572]/30 hover:shadow-sm transition-all">
        <Avatar className="h-12 w-12 shrink-0">
          {avatarUrl ? <AvatarImage src={avatarUrl} alt={user.name} /> : null}
          <AvatarFallback className="bg-[#074572] text-white text-sm font-medium">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-semibold text-zinc-900 truncate">{user.name}</p>
            {user.is_premium && <Crown size={11} className="text-amber-500" />}
            {user.ergo_verified && <ShieldCheck size={11} className="text-[#074572]" />}
            {user.coaching_license && <GraduationCap size={11} className="text-[#F85037]" />}
          </div>
          {user.position && <p className="text-xs text-zinc-500 truncate">{user.position}</p>}
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-zinc-400">
            {user.location && <span className="flex items-center gap-0.5 truncate"><MapPin size={10} />{user.location}</span>}
            {user.club && <span className="flex items-center gap-0.5 truncate"><Briefcase size={10} />{user.club}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}

function TeamCard({ team }) {
  return (
    <Link to={`/teams/${team.id}`} data-testid={`search-team-${team.id}`}>
      <div className="bg-white border border-[#E6E6E6] rounded-lg p-3 flex items-center gap-3 hover:border-[#074572]/30 hover:shadow-sm transition-all">
        <div className="w-12 h-12 rounded-md bg-[#074572]/5 flex items-center justify-center shrink-0">
          <Building2 size={20} className="text-[#074572]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-900 truncate">{team.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            {team.team_type && <Badge className="text-[10px] bg-[#074572]/5 text-[#074572] capitalize">{team.team_type}</Badge>}
            {team.location && <span className="text-[11px] text-zinc-400 flex items-center gap-0.5"><MapPin size={10} />{team.location}</span>}
          </div>
          {team.members && <p className="text-[11px] text-zinc-400 mt-0.5">{team.members.length} members</p>}
        </div>
      </div>
    </Link>
  );
}
